import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { compose } from 'redux';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alertRulesActions, alertRulesTypes } from 'app-components/flux/alert-rules';
import { makeStyles } from 'tss-react/mui';

import QButton from 'components/QButton';
import QInputSpan from 'components/QInputSpan';

const useStyles = makeStyles()((theme) => ({
  formatRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  subtitleItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  editNotificationButton: {
    textTransform: 'none',
    textDecoration: 'none',
    fontSize: 14,
    minWidth: 0,
    paddingBottom: 0,
    paddingTop: 0,
    backgroundColor: 'transparent',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: 'transparent',
    },
  },
  button: {
    margin: theme.spacing(1),
  },
  description: {
    alignItems: 'center',
  },
}));

const NotificationSubtitleLegacy = (props) => {
  const { classes } = useStyles();
  const { title, alertRule } = props;
  const dispatch = useDispatch();

  const [edit, setEdit] = useState(false);
  const [variables, setVariables] = useState({});

  const description = useMemo(() => {
    let result = alertRule.description ?? '';
    if (alertRule.inputVariables) {
      result = Object.entries(alertRule.inputVariables).reduce((descriptionTmp, [key, value]) => {
        const currentValue = alertRule.inputValues?.[key] ?? value.default;
        let valueFormatted;
        switch (value.format) {
          case 'percentage':
            valueFormatted = `${currentValue}%`;
            break;
          case 'currency':
            valueFormatted = `$${currentValue}`;
            break;
          default:
            valueFormatted = currentValue;
        }
        return descriptionTmp.replace(`%${key}%`, valueFormatted);
      }, result);
    }
    return result;
  }, [alertRule]);

  const editDescription = useMemo(() => {
    let components = [alertRule.description];
    if (alertRule.inputVariables) {
      components = Object.entries(alertRule.inputVariables).reduce(
        (templates, [key, value]) =>
          templates
            .map((template) =>
              typeof template !== 'string'
                ? template
                : template.split(`%${key}%`).reduce(
                    (items, item, index) => [
                      ...items,
                      ...(index
                        ? [
                            ...(alertRule.inputVariables?.[key]?.format === 'currency' ? ['$'] : []),
                            <QInputSpan
                              style={{
                                marginLeft: 2,
                                marginRight: 2,
                              }}
                              key={key}
                              id={key}
                              aria-label='parameter'
                              autoFocus
                              numbersOnly
                              defaultValue={alertRule.inputValues?.[key] ?? value.default}
                              onChange={(newValue) => setVariables({ ...variables, [key]: newValue })}
                            />,
                            ...(alertRule.inputVariables?.[key]?.format === 'percentage' ? ['%'] : []),
                          ]
                        : []),
                      item,
                    ],
                    [],
                  ),
            )
            .flat(),
        components,
      );
    }
    return components;
  }, [alertRule, variables]);

  // Handles opening the edit drawer
  const handleDrawerOpen = useCallback(() => {
    setEdit(true);
  }, []);

  // Handles saving the new threshold
  const handleSave = useCallback(() => {
    dispatch(
      alertRulesActions.updateAlertRule(
        alertRulesTypes.mkAlertRuleUpdate({
          id: alertRule.id,
          inputValues: {
            ...variables,
          },
        }),
        { undo: { userMessage: 'Notification settings updated.' } },
      ),
    );
    setEdit(false);
  }, [alertRule, dispatch, variables]);

  const isEditable = Boolean(alertRule.inputVariables);

  return (
    <div className={classes.formatRow}>
      <div className={classes.subtitleItem}>
        <Typography
          variant='paragraphRegular'
          display='inline'
          className={classes.description}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
            } else if (event.key === 'Escape') {
              setEdit(false);
            }
            event.stopPropagation();
          }}
        >
          {edit ? editDescription : description}
        </Typography>
        {isEditable && !edit && (
          <QButton
            id={`edit-${title}-notification`}
            variant='text'
            onClick={handleDrawerOpen}
            className={classes.editNotificationButton}
          >
            EDIT
          </QButton>
        )}
      </div>
      {isEditable && edit && (
        <Box display='flex'>
          <QButton id='continue-btn' variant='contained' onClick={handleSave} className={classes.button}>
            Save
          </QButton>
          <QButton id='back-btn' variant='outlined' onClick={() => setEdit(false)} className={classes.button}>
            Cancel
          </QButton>
        </Box>
      )}
    </div>
  );
};
NotificationSubtitleLegacy.propTypes = {
  title: PropTypes.string,
  alertRule: PropTypes.any,
};
export default compose()(NotificationSubtitleLegacy);
